import PieChart from "components/Graph/PieChart";
import React, { useMemo, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { generatePath, Link, useNavigate, useParams } from "react-router-dom";

import KhanReportLogo from "components/shared/ReportComponents/KhanReortLogo";
import { MinusPurpleIcon, PlusBlackIcon, PlusCircleIcon, PlusIcon, PlusPurpleIcon } from "Assets/icons";
import { CLASSROOM_PLAN_PREVIEW, CLASSROOM_SERVICES } from "config/paths";
import { Formik } from "formik";
import { useGetClassroomConsultationReportStepsQuery, useSubmitClassroomConsultationFeedbackMutation } from "store/services/classroom";
import LoadingIndicator from "components/shared/LoadingIndicator";
import { CLIENT_REPORT_TYPES, CONSULTATION_PLANT_REPORT_QUESTIONS, REPORT_IDS } from "config/constants";
import ReportHeader from "components/shared/ReportComponents/ReportHeader/Index";
import moment from "moment";
import { dateFormat, successMessageToast, userFullName } from "helpers/common";
import RenderQuestion from "components/shared/ReportComponents/Questions/Index";
import AttachDocumentsReports from "components/shared/ReportComponents/AttachDocumentsReports";
import TherapistSignature from "components/shared/ReportComponents/TherapistSignature";
import { reportQuestionsValidationSchema } from "utils/validation/validationHelpers";
import { object } from "yup";
import { formatReportFeedback, reportInitialValues } from "helpers/assessment";
import ReactSwitch from "react-switch";
import LoadingOverlay from "components/shared/LoadingOverlay";
import ReportError from "../components/ReportError";

const ClassroomPlan = () => {

  const { assessmentId, clientId } = useParams();

  const navigate = useNavigate();

  const [reportHeading, setReportHeading] = useState(CLIENT_REPORT_TYPES[REPORT_IDS.CLASSROOM_PLAN].title);
  const [submitStep, { isLoading: submittingStepLoading }] = useSubmitClassroomConsultationFeedbackMutation();

  const { data, isLoading, isFetching, isError, refetch } = useGetClassroomConsultationReportStepsQuery(clientId);
  const details = data?.record;
  const reportQuestions = data?.questions;

  const initialValues = useMemo(() => {
    if (!data || !reportQuestions) return {};
    let answers = data?.answers || [];
    let values = {
      documents: data?.attachments?.reportDocuments || [],
      resources: data?.assesmentData?.reportResources || [],
    };

    values = reportInitialValues(values, reportQuestions, answers);

    return values;
  }, [data, reportQuestions]);

  const validationSchema = useMemo(() => {
    if (!reportQuestions) return object();

    return reportQuestionsValidationSchema(reportQuestions);
  }, [reportQuestions])

  const handleSubmitStep = async (values) => {
    try {

      const payload = formatReportFeedback(reportHeading, reportQuestions, values);
      const response = await submitStep({ consultationId: clientId, data: payload, });

      if (!response?.error) {
        navigate(generatePath(CLASSROOM_PLAN_PREVIEW, { assessmentId, clientId }));
      };

    } catch (err) {
      console.error("Error submitting classroom consultation step", err);
    }
  };

  return (
    <>
      {" "}
      <section className="startSession position-relative pay-3">
        <Container fluid>
          {isLoading ? <LoadingIndicator /> : (
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmitStep}
              enableReinitialize
            >
              {
                ({ handleSubmit, errors, values }) => (
                  <Form onSubmit={handleSubmit}>
                    {console.log("errors", errors)}
                    {isFetching && <LoadingOverlay />}
                    <Row>
                      <Col lg="12" className="my-2">
                        <div className="cardCstm p-lg-5 rounded-2 bg-white shadow">
                          <ReportHeader
                            initialHeading={details?.report_heading || reportHeading}
                            therapistDetails={details?.PreflightOwner}
                            setHeading={setReportHeading}
                          />
                          <div className="d-flex justify-content-between">
                            <ul className="list-unstyled ps-0 mb-0 d-flex align-items-center">
                              <li className="">
                                <Link
                                  className="themeBlue fw-sbold"
                                  style={{ textDecoration: "underline", fontSize: 14 }}
                                  to={CLASSROOM_SERVICES}
                                >
                                  Client
                                </Link>
                              </li>
                              <li className="">
                                <h6 className="m-0 fw-bold themeBlue">
                                  / Classroom Plan Setup
                                </h6>
                              </li>
                            </ul>
                          </div>
                          <div className="py-2">
                            <div
                              className="cardCstm rounded-2 px-3"
                              style={{ background: "rgb(249, 249, 249)" }}
                            >
                              <div className="border-bottom p-2 row">
                                <div className="py-2 col-md-6">
                                  <div className="d-flex align-items-start gap-10 flex-wrap">
                                    <h6
                                      className="m-0 fw-sbold themeBlue"
                                      style={{ minWidth: 200, fontSize: 14 }}
                                    >
                                      Report Date:
                                    </h6>
                                    <p className="m-0 themeBlue">
                                      <span className="icn me-2">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width={18}
                                          height={20}
                                          viewBox="0 0 18 20"
                                          fill="none"
                                        >
                                          <path
                                            d="M2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V4C0 3.45 0.195833 2.97917 0.5875 2.5875C0.979167 2.19583 1.45 2 2 2H3V0H5V2H13V0H15V2H16C16.55 2 17.0208 2.19583 17.4125 2.5875C17.8042 2.97917 18 3.45 18 4V18C18 18.55 17.8042 19.0208 17.4125 19.4125C17.0208 19.8042 16.55 20 16 20H2ZM2 18H16V8H2V18ZM2 6H16V4H2V6ZM9 12C8.71667 12 8.47917 11.9042 8.2875 11.7125C8.09583 11.5208 8 11.2833 8 11C8 10.7167 8.09583 10.4792 8.2875 10.2875C8.47917 10.0958 8.71667 10 9 10C9.28333 10 9.52083 10.0958 9.7125 10.2875C9.90417 10.4792 10 10.7167 10 11C10 11.2833 9.90417 11.5208 9.7125 11.7125C9.52083 11.9042 9.28333 12 9 12ZM5 12C4.71667 12 4.47917 11.9042 4.2875 11.7125C4.09583 11.5208 4 11.2833 4 11C4 10.7167 4.09583 10.4792 4.2875 10.2875C4.47917 10.0958 4.71667 10 5 10C5.28333 10 5.52083 10.0958 5.7125 10.2875C5.90417 10.4792 6 10.7167 6 11C6 11.2833 5.90417 11.5208 5.7125 11.7125C5.52083 11.9042 5.28333 12 5 12ZM13 12C12.7167 12 12.4792 11.9042 12.2875 11.7125C12.0958 11.5208 12 11.2833 12 11C12 10.7167 12.0958 10.4792 12.2875 10.2875C12.4792 10.0958 12.7167 10 13 10C13.2833 10 13.5208 10.0958 13.7125 10.2875C13.9042 10.4792 14 10.7167 14 11C14 11.2833 13.9042 11.5208 13.7125 11.7125C13.5208 11.9042 13.2833 12 13 12ZM9 16C8.71667 16 8.47917 15.9042 8.2875 15.7125C8.09583 15.5208 8 15.2833 8 15C8 14.7167 8.09583 14.4792 8.2875 14.2875C8.47917 14.0958 8.71667 14 9 14C9.28333 14 9.52083 14.0958 9.7125 14.2875C9.90417 14.4792 10 14.7167 10 15C10 15.2833 9.90417 15.5208 9.7125 15.7125C9.52083 15.9042 9.28333 16 9 16ZM5 16C4.71667 16 4.47917 15.9042 4.2875 15.7125C4.09583 15.5208 4 15.2833 4 15C4 14.7167 4.09583 14.4792 4.2875 14.2875C4.47917 14.0958 4.71667 14 5 14C5.28333 14 5.52083 14.0958 5.7125 14.2875C5.90417 14.4792 6 14.7167 6 15C6 15.2833 5.90417 15.5208 5.7125 15.7125C5.52083 15.9042 5.28333 16 5 16ZM13 16C12.7167 16 12.4792 15.9042 12.2875 15.7125C12.0958 15.5208 12 15.2833 12 15C12 14.7167 12.0958 14.4792 12.2875 14.2875C12.4792 14.0958 12.7167 14 13 14C13.2833 14 13.5208 14.0958 13.7125 14.2875C13.9042 14.4792 14 14.7167 14 15C14 15.2833 13.9042 15.5208 13.7125 15.7125C13.5208 15.9042 13.2833 16 13 16Z"
                                            fill="#071E3E"
                                          />
                                        </svg>
                                      </span>
                                      {dateFormat(moment())}
                                    </p>
                                  </div>
                                </div>
                                <div className="py-2 col-md-6">
                                  <div className="d-flex align-items-start gap-10 flex-wrap">
                                    <h6
                                      className="m-0 fw-sbold themeBlue"
                                      style={{ minWidth: 200, fontSize: 14 }}
                                    >
                                      Clinician Name:
                                    </h6>
                                    <p className="m-0 themeBlue text-capitalize">{userFullName(details?.PreflightOwner)}</p>
                                  </div>
                                </div>
                              </div>
                              <div className="border-bottom p-2 row">
                                <div className="py-2 col-md-6">
                                  <div className="d-flex align-items-start gap-10 flex-wrap">
                                    <h6
                                      className="m-0 fw-sbold themeBlue"
                                      style={{ minWidth: 200, fontSize: 14 }}
                                    >
                                      School Name:
                                    </h6>
                                    <p className="m-0 themeBlue text-capitalize">{details?.PreflightSchool?.name || "-"}</p>
                                  </div>
                                </div>
                                <div className="py-2 col-md-6">
                                  <div className="d-flex align-items-start gap-10 flex-wrap">
                                    <h6
                                      className="m-0 fw-sbold themeBlue"
                                      style={{ minWidth: 200, fontSize: 14 }}
                                    >
                                      City/Town:
                                    </h6>
                                    <p className="m-0 themeBlue">Nevada</p>
                                  </div>
                                </div>
                              </div>
                              <div className="border-bottom p-2 row">
                                <div className="py-2 col-md-6">
                                  <div className="d-flex align-items-start gap-10 flex-wrap">
                                    <h6
                                      className="m-0 fw-sbold themeBlue"
                                      style={{ minWidth: 200, fontSize: 14 }}
                                    >
                                      Grade:
                                    </h6>
                                    <p className="m-0 themeBlue text-capitalize">
                                      {details?.grade || "-"}
                                    </p>
                                  </div>
                                </div>
                                <div className="py-2 col-md-6">
                                  <div className="d-flex align-items-start gap-10 flex-wrap">
                                    <h6
                                      className="m-0 fw-sbold themeBlue"
                                      style={{ minWidth: 200, fontSize: 14 }}
                                    >
                                      Teacher:
                                    </h6>
                                    <p className="m-0 themeBlue">{userFullName(details?.PreflightTeacher) || "-"}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            {/* <div className="d-flex align-items-baseline gap-10 justify-content-end py-3">
                              <h6 className="m-0 themeBlue fw-bold pb-2">Student #</h6>
                              {details?.clients?.length || 0}
                            </div> */}

                          </div>
                          <div className="py-2">
                            <Form>
                              <Row>
                                {data?.questions?.length > 0 &&
                                  data?.questions?.map((question, index) => {
                                    return (
                                      <div className="py-3">
                                        <RenderQuestion
                                          key={index}
                                          question={question}
                                          fieldName={question.id}
                                          showQuestion={question.id !== CONSULTATION_PLANT_REPORT_QUESTIONS.tracker}
                                        />
                                      </div>
                                    );
                                  }
                                  )}
                                {/* <Col lg="12" className="my-2 px-4">
                                  <Row
                                    className="p-2"
                                    style={{ border: "1px solid #d5d5d5", borderRadius: "10px" }}
                                  >
                                    <Col lg="10">
                                      <div className="py-3">
                                        <h6 className="m-0 themeBlue fw-bold pb-2">
                                          {" "}
                                          Area Targeted
                                        </h6>
                                        <div className="iconWithText position-relative">
                                          <textarea
                                            name=""
                                            id=""
                                            rows={3}
                                            className="form-control rounded-4 p-lg-4 h-auto bg-white"
                                          ></textarea>
                                        </div>
                                      </div>
                                      <div className="py-3">
                                        <h6 className="m-0 themeBlue fw-bold pb-2">
                                          {" "}
                                          Program and Strategies Demonstrated
                                        </h6>
                                        <div className="iconWithText position-relative">
                                          <textarea
                                            name=""
                                            id=""
                                            rows={3}
                                            className="form-control rounded-4 p-lg-4 h-auto bg-white"
                                          ></textarea>
                                        </div>
                                      </div>
                                    </Col>
                                    <Col lg="2">
                                      <div className="d-flex justify-content-center pt-4">
                                        <Button
                                          variant="transparent"
                                        >
                                          <PlusPurpleIcon />
                                        </Button>
                                      </div>
                                    </Col>
                                  </Row>
                                </Col> */}
                                <Col lg="12" className="my-2">
                                  <div className="py-3">
                                    <AttachDocumentsReports
                                      acl={"private"}
                                      name="documents"
                                      allowedTypes={["pdf", "doc", "docx", "jpg", "xlsx", "png"]}
                                    />
                                  </div>
                                </Col>
                                <Col lg="12" className="my-2">
                                  <div className="py-3">
                                    <TherapistSignature
                                      therapistDetails={details?.PreflightOwner}
                                      signature_img={details?.PreflightOwner?.therapist_detail?.signature_img}
                                      email_signature={details?.PreflightOwner?.therapist_detail?.email_signature}
                                    />
                                  </div>
                                </Col>
                                <Col lg="12" className="my-2">
                                  <div className="py-3">
                                    <KhanReportLogo />
                                  </div>
                                </Col>
                              </Row>
                            </Form>
                          </div>
                          <div className="btnWrpper mt-3">
                            <ReportError />
                            <div className="d-flex align-items-center gap-10">
                              <Button
                                type="submit"
                                className="d-flex align-items-center justify-content-center commonBtn fw-sbold"
                                style={{ background: "#3DA0AA", borderColor: "#3DA0AA" }}
                                disabled={submittingStepLoading || details?.status == "complete"}
                              >
                                {submittingStepLoading ? <LoadingIndicator /> : "Preview"}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                )}
            </Formik>
          )}
        </Container>
      </section>
    </>
  );
};

export default ClassroomPlan;
